"use client";

import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useAdminOptional } from "@/lib/admin/context";
import { iadminUrl, buildIadminFullUrl } from "@/lib/admin/iadmin-links";
import { CHROME_AREAS } from "@/lib/layouts/types";
import type { AlignmentIssue } from "@/lib/layouts/registry";
import { KNOWN_WINDOW_SYMBOLS } from "@/components/cms/registry/variants";
import { Icon } from "@/lib/icons/Icon";
import styles from "./AlignmentChecker.module.css";

/**
 * AlignmentCheckerView — client-side renderer banneru developera.
 *
 * **Łączy dwa źródła issue'ów:**
 *   1. `initialIssues` — z server-side `findAlignmentIssues()` (statyczne,
 *      walidacja DB ↔ React registry, SSO config itp.)
 *   2. Dynamic issues z `AdminContext` — np. selekcja okna w panelu
 *      "Nieopublikowane okna" (`window_no_allowed_areas` /
 *      `window_no_compatible_areas`). Issues reagują na zmianę selekcji.
 *
 * **Pozycja:** mount przez `AlignmentChecker` (Server Component wrapper)
 * w `PublicLayout`. View renderuje cały sticky banner.
 *
 * **Dev-mode only** — wrapper już sprawdza `NODE_ENV`, ten plik zakłada
 * że jeśli się rendere'uje, jesteśmy w dev mode.
 *
 * **`adminPanelUrl`** przekazany jako prop (zamiast importu server-only
 * `getApiBaseUrl()`) — wykorzystywany do budowy docs URL'i.
 */
export function AlignmentCheckerView({
  initialIssues,
  adminPanelUrl,
}: AlignmentCheckerViewProps) {
  const admin = useAdminOptional();
  /* Tłumaczenia dev banneru — gdy admin context jest dostępny (zawsze powinien
     być, bo AlignmentChecker renderowany w PublicLayout > AdminProvider). */
  const tAlign = admin?.t.alignmentChecker;

  /* Handler przycisku "Utwórz paczkę" - wywolanie /api/dev/generate-package,
     download response body jako plik. Prevention double-click przez in-flight guard. */
  const [isGeneratingPackage, setIsGeneratingPackage] = useState(false);
  const handleGeneratePackage = async () => {
    if (isGeneratingPackage) return;
    if (!tAlign?.generatePackage) return;

    const tGen = tAlign.generatePackage;
    setIsGeneratingPackage(true);
    const toastId = toast.loading(tGen.inProgress);

    try {
      const response = await fetch("/api/dev/generate-package", { method: "POST" });

      if (!response.ok) {
        /* Backend zwraca JSON z error + message dla znanych bledow (403, 409).
           Dla nieznanych - fallback do statusText. */
        let errorMessage = response.statusText;
        try {
          const payload = (await response.json()) as { error?: string; message?: string };
          if (payload.error === "FORBIDDEN") errorMessage = tGen.errorForbidden;
          else if (payload.error === "BUILD_MISSING") errorMessage = tGen.errorBuildMissing;
          else if (payload.message) errorMessage = payload.message;
        } catch {
          /* JSON parse fail - zostaje statusText. */
        }
        toast.error(tGen.errorGeneric(errorMessage), { id: toastId });
        return;
      }

      /* Extract filename z Content-Disposition header (backend generuje
         imperial-cms-YYYYMMDD-HHMMSS.zip); fallback gdy header nietypowy. */
      const disposition = response.headers.get("Content-Disposition") ?? "";
      const filenameMatch = disposition.match(/filename="([^"]+)"/);
      const filename = filenameMatch?.[1] ?? "imperial-cms.zip";

      /* Streamed ZIP -> Blob -> object URL -> hidden <a download> click.
         To standardowy patern dla programowego downloadu binarnego content'u
         w browserze bez opuszczania biezacej strony. */
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(tGen.success(filename), { id: toastId });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      toast.error(tGen.errorGeneric(message), { id: toastId });
    } finally {
      setIsGeneratingPackage(false);
    }
  };

  /* Dynamic issue: wybrane okno z panelu "Nieopublikowane okna" + jego stan
     skonfigurowanych obszarów. Memoized po inputach z context'u — nie
     rebuilduje listy issues niepotrzebnie. */
  const dynamicIssues = useMemo<AlignmentIssue[]>(() => {
    if (!admin) return [];
    const {
      selectedWindowId,
      pageWindows,
      unpublishedWindows,
      currentLayout,
      currentPageType,
      availableLayouts,
    } = admin;
    if (selectedWindowId === null) return [];

    /* Selekcja może wskazywać okno z `pageWindows` (slot na stronie) lub
       `unpublishedWindows` (draft w panelu) — szukamy w obu. */
    const selected =
      pageWindows.find((w) => w.id === selectedWindowId) ??
      unpublishedWindows.find((w) => w.id === selectedWindowId);
    if (!selected) return [];

    /* Resolve current layout z backend-fetched listy availableLayouts
       (filtruje po page type). null gdy brak skonfigurowanych — w takim
       wypadku banner `no_layouts_assigned` (z findAlignmentIssues) już
       sygnalizuje root problem; skip dynamic window issue. */
    const candidateLayouts = availableLayouts.filter((l) =>
      currentPageType === "catalog" ? l.supports_subpage : l.supports_homepage,
    );
    const selectedMeta =
      candidateLayouts.find((l) => l.symbol === currentLayout) ??
      candidateLayouts[0] ??
      null;
    if (!selectedMeta) return [];

    const layoutLabel = pickLocaleLabelLocal(selectedMeta.name, selectedMeta.symbol);
    const availableAreaSymbols: string[] = [
      ...selectedMeta.areas.map((a) => a.area_key),
      ...CHROME_AREAS,
    ];
    const compatibleAreas = selected.allowedAreas.filter((a) =>
      availableAreaSymbols.includes(a),
    );

    if (selected.allowedAreas.length === 0) {
      return [
        {
          type: "window_no_allowed_areas",
          windowId: selected.id,
          windowName: selected.name,
          componentSymbol: selected.componentSymbol,
          message: admin.t.alignmentChecker.issue.windowNoAllowedAreas(selected.name),
          hint:
            "Otwórz panel: Imperial Administrator > System administracyjny > Konfiguracja domen > " +
            "[twoja domena] > Komponenty. Znajdź komponent typu 'Okno' powiązany z " +
            `tym oknem (component_symbol=${selected.componentSymbol}), kliknij ikonę ` +
            "Edytuj, w sekcji 'Dozwolone obszary (dla okien)' zaznacz obszary w których " +
            "to okno może być umieszczone, zapisz. Imperial CMS od razu zacznie czytać nowe " +
            "ustawienia.",
          docsAnchor: "lyt-areas",
        },
      ];
    }

    if (compatibleAreas.length === 0) {
      return [
        {
          type: "window_no_compatible_areas",
          windowId: selected.id,
          windowName: selected.name,
          componentSymbol: selected.componentSymbol,
          allowedAreas: selected.allowedAreas,
          availableAreas: availableAreaSymbols,
          layoutName: layoutLabel,
          message: admin.t.alignmentChecker.issue.windowNoMatchInLayout(selected.name, layoutLabel),
          hint:
            `Dozwolone obszary okna: ${selected.allowedAreas.join(", ")}. ` +
            `Obszary w układzie "${layoutLabel}": ${availableAreaSymbols.join(", ")}. ` +
            "Brak przecięcia — albo dodaj brakujące area do układu w panelu Imperial, albo zaktualizuj allowed_areas okna.",
          docsAnchor: "lyt-areas",
        },
      ];
    }

    return [];
  }, [admin]);

  /* Dodatkowe dynamic issues — komponenty okien (componentSymbol) bez React
     renderera w `KNOWN_WINDOW_SYMBOLS` (z `components/cms/registry/variants.tsx`).
     Skanuje unique component symbols z OBU zbiorów (`pageWindows` +
     `unpublishedWindows`) + `windowContents` (sprawdza czy backend faktycznie
     ma działającą metodę przed alertem o brak renderera). */
  const rendererIssues = useMemo<AlignmentIssue[]>(() => {
    if (!admin) return [];
    const knownSet = new Set<string>(KNOWN_WINDOW_SYMBOLS);
    /* Map componentSymbol -> [pierwsze 3 nazwy okien tego typu]. Tylko okna
       których backend POTWIERDZA że ma getApiContent (meta.missing_method=false).
       Bo gdy backend nie ma metody, problem jest backend-side, nie React. */
    const missingRenderers = new Map<string, string[]>();
    const seenWindowIds = new Set<number>();
    for (const w of [...admin.pageWindows, ...admin.unpublishedWindows]) {
      if (seenWindowIds.has(w.id)) continue;
      seenWindowIds.add(w.id);
      const sym = w.componentSymbol;
      if (knownSet.has(sym)) continue;
      const contentPromise = admin.windowContents.get(w.id);
      /* Bez wpisu (np. drafty bez fetcha) — pomiń. Inaczej kaskadowałoby
         z fetch failures. */
      if (!contentPromise) continue;
      /* Uwaga: `contentPromise` to `Promise<WindowContentResponse | null>` — nie da się
         tu odczytać `.meta.missing_class` synchronicznie w obrębie useMemo. Wcześniej
         filtr odrzucał okna gdzie backend NIE MA `getApiContent` (missing_class/method)
         żeby nie raportować ich jako brak React renderera. Bez tego filtru poniższy
         alignment issue („react_renderer_missing”) wystąpi też dla okien w których
         winnym jest backend — ale ten przypadek jest pokrywany osobnym issuem
         (`api_method_missing`) generowanym server-side. Akceptowalna duplikacja
         alternatywą do `use(promise)` (suspend per okno). */
      const examples = missingRenderers.get(sym) ?? [];
      if (examples.length < 3) examples.push(w.name);
      missingRenderers.set(sym, examples);
    }
    const out: AlignmentIssue[] = [];
    for (const [sym, examples] of missingRenderers.entries()) {
      out.push({
        type: "react_renderer_missing",
        componentSymbol: sym,
        windowExamples: examples,
        message: admin.t.alignmentChecker.issue.noRendererInReactCms(sym),
        hint:
          `Dodaj komponent W${snakeToPascalForHint(sym)} w ` +
          `\`components/windows/W${snakeToPascalForHint(sym)}/W${snakeToPascalForHint(sym)}.tsx\` ` +
          `+ zarejestruj w \`windowVariants.defaults\` w ` +
          `\`components/cms/registry/variants.tsx\`. Wzorzec: \`components/windows/WMenu/\`.`,
        docsAnchor: "rest-api",
      });
    }
    return out;
  }, [admin]);

  /* Dynamic issue: aktywny szablon domeny nie ma layoutów dla bieżącego page
     type'u. Czytane z `AdminContext.activeTemplate` + counts (propagowane przez
     PublicLayout z meta backend'u: `available_layouts_count_homepage/subpage`).

     **Kiedy fires:** activeTemplate jest ustawiony (nie null) ORAZ count dla
     currentPageType === 0. Backend wtedy zwraca `layoutSource='invalid_for_template'`
     i React-CMS blokuje render — banner deweloperski pokazuje dlaczego.

     **Skip gdy `activeTemplate === null`** — to inny przypadek (domena bez
     szablonu); pokrywa go statyczny issue z `findAlignmentIssues()`. */
  const activeTemplateIssues = useMemo<AlignmentIssue[]>(() => {
    if (!admin) return [];
    const { activeTemplate, currentPageType, availableLayoutsCountHomepage, availableLayoutsCountSubpage } = admin;
    if (activeTemplate === null) return [];
    const count = currentPageType === "homepage"
      ? availableLayoutsCountHomepage
      : availableLayoutsCountSubpage;
    if (count > 0) return [];

    const pageTypeLabel = currentPageType === "homepage"
      ? admin.t.alignmentChecker.pageType.homepage
      : admin.t.alignmentChecker.pageType.subpage;
    return [
      {
        type: "active_template_missing_page_type_layouts",
        activeTemplate,
        pageType: currentPageType,
        message: admin.t.alignmentChecker.issue.noLayoutsForTemplate(activeTemplate, pageTypeLabel),
        hint:
          `Otwórz panel: Imperial Administrator > System administracyjny > Konfiguracja domen > ` +
          `[twoja domena] > Szablony > ${activeTemplate} > Layouty > Dodaj. ` +
          `Przypisz przynajmniej jeden layout z flagą supports_${currentPageType === "homepage" ? "homepage" : "subpage"}=1.`,
        docsAnchor: "lyt-db",
      },
    ];
  }, [admin]);

  const allDynamic = [
    ...dynamicIssues,
    ...rendererIssues,
    ...activeTemplateIssues,
  ];

  /* Hide-toggle przez AdminContext.devBannerHidden — admin może schować
     banner przyciskiem w AdminTopBar (lokalna preferencja w localStorage). */
  if (admin?.devBannerHidden) return null;

  const issues = [...initialIssues, ...allDynamic];
  const isAllGood = issues.length === 0;
  const ordered = [...issues].sort((a, b) => severity(b) - severity(a));

  const buildDocsUrl = (anchor?: string): string => {
    const base = buildIadminFullUrl(adminPanelUrl, iadminUrl.documentation({ doc: "react_cms" }));
    return anchor ? `${base}#${anchor}` : base;
  };

  /* Closures z `t.alignmentChecker.*` — labelFor i docsAnchorLabel są module-scope
     bo używamy ich również w teście snapshotów. Tutaj wstrzykujemy tłumaczenia. */
  const labelFor = buildLabelFor(tAlign?.issueLabels);
  const docsAnchorLabel = buildDocsAnchorLabel(tAlign?.docsAnchor);

  return (
    <aside
      className={styles.banner}
      data-status={isAllGood ? "ok" : "issues"}
      role="status"
      aria-label={tAlign?.bannerAriaLabel ?? "Developer banner"}
    >
      {/* Inline close — szybkie ukrycie banneru bez konieczności szukania gear
          icon w AdminTopBar. Stan persistowany w localStorage przez
          AdminContext.setDevBannerHidden; przywrócenie nadal przez
          <DevBannerToggle> w AdminTopBar. */}
      {admin && (
        <button
          type="button"
          className={styles.closeButton}
          onClick={() => admin.setDevBannerHidden(true)}
          title={tAlign?.closeButton.title ?? "Hide developer banner"}
          aria-label={tAlign?.closeButton.ariaLabel ?? "Hide developer banner"}
        >
          <Icon name="x" />
        </button>
      )}
      <div className={styles.devBadge}>
        <Icon name="gear" className={styles.devBadgeIcon} />
        {tAlign?.devBadge}
        <a
          href={buildDocsUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.devBadgeLink}
        >
          <Icon name="arrow-square-out" />
          {tAlign?.fullDocs}
        </a>
        {/* IADMIN quick links — natywny <details>/<summary> dropdown.
            Hardcoded lista najczęściej używanych admin pages dla CMS.
            Click outside zamyka standardowo (details native behavior). */}
        <details className={styles.iadminMenu}>
          <summary className={styles.iadminMenuTrigger}>
            <Icon name="list" />
            <span>{tAlign?.openInImperialAdmin}</span>
            <Icon name="caret-down" />
          </summary>
          <div className={styles.iadminMenuPanel}>
            {/* Bieżąca domena — TYLKO gdy mamy id_domain z backendu.
                Deep-linki do per-domain management (komponenty/szablony/layouty
                przypisane do TEJ domeny, nie globalny rejestr). */}
            {admin?.idDomain != null && (
              <IadminMenuSection
                title={tAlign?.sectionDomain(admin.domain) ?? `Domain (${admin.domain})`}
                adminPanelUrl={adminPanelUrl}
                links={[
                  {
                    raw: buildIadminFullUrl(adminPanelUrl, iadminUrl.systemSys({
                      sys: "domains",
                      params: { action: "components", id: admin.idDomain },
                    })),
                    label: tAlign?.linkDomainComponents ?? "",
                  },
                  {
                    raw: buildIadminFullUrl(adminPanelUrl, iadminUrl.systemSys({
                      sys: "domains",
                      params: { action: "containers_component", id: admin.idDomain },
                    })),
                    label: tAlign?.linkComponentContainers ?? "",
                  },
                  {
                    raw: buildIadminFullUrl(adminPanelUrl, iadminUrl.systemSys({
                      sys: "domains",
                      params: { action: "templates", id: admin.idDomain },
                    })),
                    label: tAlign?.linkDomainTemplates ?? "",
                  },
                  ...(admin.idActiveDomainTemplate != null
                    ? [
                        {
                          raw: buildIadminFullUrl(adminPanelUrl, iadminUrl.systemSys({
                            sys: "domains",
                            params: {
                              action: "layouts",
                              id: admin.idDomain,
                              id_element: admin.idActiveDomainTemplate,
                            },
                          })),
                          label: tAlign?.linkTemplateLayouts(admin.activeTemplate) ?? "",
                        },
                      ]
                    : []),
                  {
                    raw: buildIadminFullUrl(adminPanelUrl, iadminUrl.systemSys({
                      sys: "domains",
                      params: { action: "languages", id: admin.idDomain },
                    })),
                    label: tAlign?.linkDomainLanguages ?? "",
                  },
                ]}
              />
            )}
            <IadminMenuSection
              title={tAlign?.sectionCmsRegistry ?? "CMS"}
              adminPanelUrl={adminPanelUrl}
              links={[
                { sys: "templates", label: tAlign?.linkTemplates ?? "" },
                { sys: "layouts", label: tAlign?.linkLayouts ?? "" },
                { sys: "components", label: tAlign?.linkComponents ?? "" },
                { sys: "containers", label: tAlign?.linkContainers ?? "" },
              ]}
            />
            <IadminMenuSection
              title={tAlign?.sectionRestApi ?? "REST API"}
              adminPanelUrl={adminPanelUrl}
              links={[
                { adminPage: "iapi", label: tAlign?.linkApiKeys ?? "" },
              ]}
            />
            <IadminMenuSection
              title={tAlign?.sectionDocs ?? "Docs"}
              adminPanelUrl={adminPanelUrl}
              links={[
                {
                  raw: buildIadminFullUrl(adminPanelUrl, iadminUrl.documentation({ doc: "react_cms" })),
                  label: tAlign?.linkReactCmsDocs ?? "",
                },
              ]}
            />
          </div>
        </details>
        {/* DEV-only toggle dla mock-drzewa w WDropDownMenu. Click przełącza
            wstrzykiwanie sztucznego L1 z 5-poziomowym drzewem testowym
            (`AdminContext.devMockDropDownTree`, persistowany w localStorage).
            Banner wewnątrz dropdown'a informuje że to dev-only test data. */}
        {admin && (
          <button
            type="button"
            className={styles.devBadgeToggle}
            data-active={admin.devMockDropDownTree ? "on" : "off"}
            onClick={() => admin.setDevMockDropDownTree(!admin.devMockDropDownTree)}
            title={
              admin.devMockDropDownTree
                ? admin.t.topBar.devMockDropDownTreeToggle.titleOn
                : admin.t.topBar.devMockDropDownTreeToggle.titleOff
            }
            aria-label={
              admin.devMockDropDownTree
                ? admin.t.topBar.devMockDropDownTreeToggle.ariaOn
                : admin.t.topBar.devMockDropDownTreeToggle.ariaOff
            }
            aria-pressed={admin.devMockDropDownTree}
          >
            <Icon name="list-bullets" />
            <span>Mock menu L5</span>
            <span aria-hidden="true" className={styles.devBadgeToggleState}>
              {admin.devMockDropDownTree ? "ON" : "OFF"}
            </span>
          </button>
        )}
        {/* Generator paczki instalacyjnej - button POST /api/dev/generate-package.
            Response stream'uje ZIP prosto do downloadu (bez zapisu w data/). */}
        {tAlign?.generatePackage && (
          <button
            type="button"
            className={styles.devBadgeToggle}
            onClick={handleGeneratePackage}
            disabled={isGeneratingPackage}
            title={tAlign.generatePackage.buttonTitle}
            aria-label={tAlign.generatePackage.buttonLabel}
            aria-busy={isGeneratingPackage}
          >
            <Icon name="download-simple" />
            <span>{tAlign.generatePackage.buttonLabel}</span>
          </button>
        )}
      </div>
      {/* Context strip — szybki podgląd kluczowych wartości runtime'u
          żeby dev nie musiał zaglądać w DevTools żeby zobaczyć z jakim
          template'em / page typem / locale aktualnie pracuje. Wszystkie
          z AdminContext (propagowane z PublicLayout meta). */}
      {admin && tAlign && (
        <div className={styles.contextStrip}>
          <span className={styles.contextItem}>
            <span className={styles.contextLabel}>{tAlign.contextLabel.domain}</span>
            <strong className={styles.contextValue}>
              {admin.domain}
              {admin.idDomain !== null && (
                <span className={styles.contextValueDim}> #{admin.idDomain}</span>
              )}
            </strong>
          </span>
          <span className={styles.contextItem}>
            <span className={styles.contextLabel}>{tAlign.contextLabel.template}</span>
            <strong className={styles.contextValue}>
              {admin.activeTemplate ?? tAlign.contextNone}
            </strong>
          </span>
          <span className={styles.contextItem}>
            <span className={styles.contextLabel}>{tAlign.contextLabel.pageType}</span>
            <strong className={styles.contextValue}>
              {admin.currentPageType}
            </strong>
          </span>
          <span className={styles.contextItem}>
            <span className={styles.contextLabel}>{tAlign.contextLabel.layout}</span>
            <strong className={styles.contextValue}>
              {admin.currentLayout}
            </strong>
          </span>
          <span className={styles.contextItem}>
            <span className={styles.contextLabel}>{tAlign.contextLabel.locale}</span>
            <strong className={styles.contextValue}>{admin.locale}</strong>
          </span>
          <span className={styles.contextItem}>
            <span className={styles.contextLabel}>{tAlign.contextLabel.layoutsForTemplate}</span>
            <strong className={styles.contextValue}>
              {admin.availableLayoutsCountHomepage} hp /{" "}
              {admin.availableLayoutsCountSubpage} sub
            </strong>
          </span>
        </div>
      )}
      <header className={styles.header}>
        <span className={styles.icon} aria-hidden="true">
          <Icon name={isAllGood ? "check-circle" : "warning"} />
        </span>
        <strong className={styles.title}>
          {isAllGood
            ? tAlign?.headerOk
            : tAlign?.headerIssues(issues.length)}
        </strong>
        <span className={styles.subtitle}>
          {tAlign?.subtitleEnv(process.env.NODE_ENV ?? "")}
        </span>
      </header>
      {isAllGood ? (
        <p className={styles.okMessage}>
          {tAlign?.okMessage}
        </p>
      ) : (
        <ul className={styles.list}>
          {ordered.map((issue, idx) => (
            <li key={idx} className={styles.item} data-issue-type={issue.type}>
              <div className={styles.itemHead}>
                <span className={styles.badge} data-severity={severityLabel(issue)}>
                  {labelFor(issue)}
                </span>
                <span className={styles.message}>{issue.message}</span>
              </div>
              <div className={styles.hint}>{issue.hint}</div>
              {/* Bezpośredni link do strony admina Imperial gdzie issue można naprawić.
                  Aktualnie tylko `window_no_container_mapping` korzysta — pole `actionUrl`
                  dodawane warunkowo per typ issue'a. */}
              {"actionUrl" in issue && issue.actionUrl && (
                <a
                  href={issue.actionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.itemDocsLink}
                >
                  <Icon name="arrow-square-out" />
                  {tAlign?.actionOpenAddContainerMapping}
                </a>
              )}
              <a
                href={buildDocsUrl(issue.docsAnchor)}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.itemDocsLink}
              >
                <Icon name="arrow-square-out" />
                {tAlign?.readMoreLinkPrefix} — {docsAnchorLabel(issue.docsAnchor)}
              </a>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

export type AlignmentCheckerViewProps = {
  initialIssues: AlignmentIssue[];
  adminPanelUrl: string;
};

/* ─── IADMIN quick links dropdown ───────────────────────────────────── */

/**
 * Sekcja IADMIN dropdown'a — pogrupowane szybkie linki do najczęściej
 * używanych admin pages. Hardcoded URLs (admin_page + opcjonalnie sys).
 *
 * Wsparcie trzech form linku:
 *   - `{ sys: "X" }`         -> ?admin_page=system_administration&sys=X
 *   - `{ adminPage: "X" }`   -> ?admin_page=X
 *   - `{ raw: "https://..." }` -> użyj URL'a literalnie (deep-link)
 */
type IadminLink =
  | { sys: string; label: string }
  | { adminPage: string; label: string }
  | { raw: string; label: string };

function IadminMenuSection({
  title,
  adminPanelUrl,
  links,
}: {
  title: string;
  adminPanelUrl: string;
  links: IadminLink[];
}) {
  const buildHref = (link: IadminLink): string => {
    if ("raw" in link) return link.raw;
    if ("sys" in link) {
      return buildIadminFullUrl(adminPanelUrl, iadminUrl.systemSys({ sys: link.sys }));
    }
    return buildIadminFullUrl(adminPanelUrl, iadminUrl.adminPage({ page: link.adminPage }));
  };
  return (
    <div className={styles.iadminMenuSection}>
      <div className={styles.iadminMenuSectionTitle}>{title}</div>
      {links.map((link, idx) => (
        <a
          key={idx}
          href={buildHref(link)}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.iadminMenuLink}
        >
          <Icon name="arrow-square-out" className={styles.iadminMenuLinkIcon} />
          <span>{link.label}</span>
        </a>
      ))}
    </div>
  );
}

/* ─── Helpers ───────────────────────────────────────────────────────── */

function severity(issue: AlignmentIssue): number {
  switch (issue.type) {
    case "fetch_failed":
      return 100;
    case "cms_frontend_url_not_configured":
      return 95;
    case "no_layouts_assigned":
      return 82;
    case "active_template_missing_page_type_layouts":
      /* Krytyczny — strona faktycznie nie renderuje się dla tego page type'u.
         Wyższe niż "missing_in_db" (registry alignment to issue dev'a, a
         missing-page-type-layouts blokuje konkretny widok). */
      return 92;
    case "orphan_in_db":
    case "missing_in_db":
      return 80;
    case "window_no_allowed_areas":
    case "window_no_compatible_areas":
      return 70;
    case "container_not_registered":
      return 65;
    case "react_renderer_missing":
      return 68;
    case "window_no_container_mapping":
      /* Visitor dostaje fallback `clear` więc strona dalej działa, ale admin
         powinien skonfigurować mapping żeby okna miały zamierzony wrapper.
         Wyższa waga niż container_not_registered (problem konfiguracyjny w DB,
         nie braku implementacji w React). */
      return 72;
    case "catalog_no_container_mapping":
      /* Analog window_no_container_mapping ale dla treści katalogu. Visitor
         dostaje fallback `catalog_empty` — strona działa, ale brakuje wrappera
         z H1 SEO (catalog_default) jeśli admin tego oczekuje. */
      return 72;
    case "render_component_mismatch":
      return 60;
    case "area_missing_in_react":
    case "area_missing_in_db":
      return 40;
    default:
      return 0;
  }
}

function severityLabel(issue: AlignmentIssue): "critical" | "high" | "medium" | "low" {
  const s = severity(issue);
  if (s >= 100) return "critical";
  if (s >= 70) return "high";
  if (s >= 60) return "medium";
  return "low";
}

/** Buduje label dla badge — używa tłumaczeń z admin context (gdy dostępne). */
function buildLabelFor(
  labels: import("@/lib/i18n/admin").AdminMessagesShape["alignmentChecker"]["issueLabels"] | undefined,
): (issue: AlignmentIssue) => string {
  return (issue) => {
    switch (issue.type) {
      case "fetch_failed":
        return "FETCH FAILED";
      case "missing_in_db":
        return "MISSING IN DB";
      case "orphan_in_db":
        return "ORPHAN IN DB";
      case "render_component_mismatch":
        return "RENDER MISMATCH";
      case "area_missing_in_react":
        return "AREA MISSING IN REACT";
      case "area_missing_in_db":
        return "AREA MISSING IN DB";
      case "window_no_allowed_areas":
        return labels?.window_no_allowed_areas ?? "WINDOW WITHOUT ALLOWED_AREAS";
      case "window_no_compatible_areas":
        return labels?.window_no_match_in_layout ?? "WINDOW NO MATCH IN LAYOUT";
      case "no_layouts_assigned":
        return labels?.no_layouts_for_domain ?? "NO LAYOUTS FOR DOMAIN";
      case "active_template_missing_page_type_layouts":
        return labels?.no_layouts_for_template ?? "NO LAYOUTS FOR TEMPLATE";
      case "container_not_registered":
        return labels?.container_not_registered ?? "CONTAINER NOT REGISTERED";
      case "react_renderer_missing":
        return labels?.no_renderer_in_react_cms ?? "NO RENDERER IN REACT-CMS";
      case "cms_frontend_url_not_configured":
        return labels?.cms_frontend_url_not_set ?? "CMS FRONTEND URL NOT SET";
      case "window_no_container_mapping":
        return labels?.windows_without_container_mapping ?? "CONTAINER MAPPING MISSING";
      case "catalog_no_container_mapping":
        return labels?.catalog_without_container_mapping ?? "CATALOG CONTAINER MAPPING MISSING";
    }
  };
}

/** `w_menu_level_one` -> `MenuLevelOne` — pomocnik dla auto-generated hint'ów. */
function snakeToPascalForHint(snake: string): string {
  return snake
    .replace(/^w_/, "")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}

/**
 * Lokalny wybór labelu z multi-locale obiektu (`{pl: "...", en: "..."}`).
 * Preferencja: `pl` -> pierwszy nie-pusty -> `fallback`.
 */
function pickLocaleLabelLocal(
  locales: Record<string, string | undefined> | null | undefined,
  fallback: string,
): string {
  if (!locales) return fallback;
  if (typeof locales.pl === "string" && locales.pl.trim() !== "") return locales.pl;
  for (const v of Object.values(locales)) {
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return fallback;
}

/** Buduje label dla docs anchor — używa tłumaczeń z admin context (gdy dostępne). */
function buildDocsAnchorLabel(
  labels: import("@/lib/i18n/admin").AdminMessagesShape["alignmentChecker"]["docsAnchor"] | undefined,
): (anchor: AlignmentIssue["docsAnchor"]) => string {
  return (anchor) => {
    switch (anchor) {
      case "config":
        return labels?.configCors ?? "Configuration (CORS, API keys)";
      case "alignment-checker":
        return labels?.issueTypes ?? "AlignmentChecker — issue types reference";
      case "lyt-add":
        return labels?.addLayoutArea ?? "How to add a layout / area";
      case "lyt-db":
        return labels?.layoutsDbBinding ?? "Layouts — DB binding";
      case "lyt-areas":
        return labels?.areasDocs ?? "Areas (body + chrome + catalog content)";
      case "rest-api":
        return labels?.restApiIntegration ?? "REST API integration";
      case "tpl-db":
        return labels?.templatesDbBinding ?? "Templates — DB binding";
    }
  };
}
